import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MessageSquare, Heart, Flag, Trash2, CornerDownRight, Send } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}

const firstName = (name) => (name || "").trim().split(/\s+/)[0] || "";

function renderText(text) {
  const parts = (text || "").split(/(@[A-Za-zÀ-ÿ0-9_]+)/g);
  return parts.map((p, i) =>
    p.startsWith("@")
      ? <span key={i} className="font-semibold text-[#E05638]">{p}</span>
      : <span key={i}>{p}</span>
  );
}

function Avatar({ name }) {
  const letter = (name || "?").trim().charAt(0).toUpperCase();
  return (
    <div className="w-9 h-9 rounded-full bg-[#E05638]/10 text-[#E05638] font-bold grid place-items-center shrink-0 text-sm">{letter}</div>
  );
}

function CommentItem({ c, isReply, canDelete, onLike, onReport, onDelete, onReply, replyOpen, replyText, setReplyText, submitReply, sending }) {
  return (
    <div className={isReply ? "pl-5 border-l-2 border-slate-100" : ""} data-testid={`comment-${c.id}`}>
      <div className="flex gap-3">
        <Avatar name={c.author_name} />
        <div className="flex-1 min-w-0">
          <div className="bg-slate-50 rounded-2xl px-4 py-2.5">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-slate-800 text-sm">{c.author_name}</span>
              <span className="text-xs text-slate-400">{timeAgo(c.created_at)}</span>
            </div>
            <p className="text-sm text-slate-700 mt-0.5 whitespace-pre-line break-words">{renderText(c.text)}</p>
          </div>
          <div className="flex items-center gap-4 mt-1.5 px-2 text-xs">
            <button onClick={() => onLike(c)} className={`inline-flex items-center gap-1 font-medium transition-colors ${c.liked_by_me ? "text-[#E05638]" : "text-slate-500 hover:text-[#E05638]"}`} data-testid={`comment-like-${c.id}`}>
              <Heart size={13} className={c.liked_by_me ? "fill-[#E05638]" : ""} /> {c.likes > 0 ? c.likes : "J'aime"}
            </button>
            {!isReply && (
              <button onClick={() => onReply(c)} className="inline-flex items-center gap-1 font-medium text-slate-500 hover:text-slate-800" data-testid={`comment-reply-${c.id}`}>
                <CornerDownRight size={13} /> Répondre
              </button>
            )}
            <button onClick={() => onReport(c)} className="inline-flex items-center gap-1 font-medium text-slate-400 hover:text-amber-600" data-testid={`comment-report-${c.id}`}>
              <Flag size={13} /> Signaler
            </button>
            {canDelete && (
              <button onClick={() => onDelete(c)} className="inline-flex items-center gap-1 font-medium text-slate-400 hover:text-rose-600" data-testid={`comment-delete-${c.id}`}>
                <Trash2 size={13} /> Supprimer
              </button>
            )}
          </div>
          {replyOpen === c.id && (
            <div className="flex gap-2 mt-2">
              <Textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} rows={2} placeholder="Votre réponse…" className="text-sm" data-testid={`comment-reply-input-${c.id}`} />
              <Button size="sm" onClick={() => submitReply(c.id)} disabled={sending} className="bg-[#E05638] hover:bg-[#c94a2f] self-end" data-testid={`comment-reply-submit-${c.id}`}><Send size={14} /></Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function PressComments({ slug }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [replyOpen, setReplyOpen] = useState(null);
  const [replyText, setReplyText] = useState("");

  const load = useCallback(() => {
    api.get(`/press-review/${slug}/comments`).then((r) => {
      setItems(r.data.items || []); setTotal(r.data.total || 0);
    }).catch(() => {});
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  const post = async (parentId = null, body = text) => {
    const t = (body || "").trim();
    if (t.length < 2) { toast.error("Votre commentaire est trop court."); return; }
    setSending(true);
    try {
      await api.post(`/press-review/${slug}/comments`, { text: t, parent_id: parentId });
      if (parentId) { setReplyText(""); setReplyOpen(null); } else { setText(""); }
      load();
      toast.success("Commentaire publié");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  const like = async (c) => {
    try {
      const { data } = await api.post(`/press-review/comments/${c.id}/like`);
      setItems((arr) => arr.map((x) => x.id === c.id ? { ...x, likes: data.likes, liked_by_me: data.liked } : x));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const report = async (c) => {
    try {
      await api.post(`/press-review/comments/${c.id}/report`);
      // Le commentaire signalé est masqué du public jusqu'à décision de l'admin
      setItems((arr) => arr.filter((x) => x.id !== c.id && x.parent_id !== c.id));
      setTotal((n) => Math.max(0, n - 1));
      toast.success("Merci, ce commentaire a été signalé et masqué en attendant la modération.");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (c) => {
    try { await api.delete(`/press-review/comments/${c.id}`); setItems((arr) => arr.filter((x) => x.id !== c.id && x.parent_id !== c.id)); setTotal((n) => Math.max(0, n - 1)); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const roots = items.filter((c) => !c.parent_id);
  const repliesOf = (id) => items.filter((c) => c.parent_id === id);
  const isAdmin = !!user && ["admin", "super_admin", "manager"].includes(user.role);

  return (
    <section className="mt-12 pt-8 border-t border-slate-100" data-testid="press-comments">
      <h2 className="font-heading text-xl font-bold text-slate-900 flex items-center gap-2">
        <MessageSquare size={18} className="text-[#E05638]" /> Discussion {total > 0 && <span className="text-slate-400 font-normal">({total})</span>}
      </h2>
      <p className="text-sm text-slate-500 mt-1">Cet article vous fait réagir ? Donnez votre avis et échangez avec les habitants du 28.</p>

      {user ? (
        <div className="mt-4 flex gap-3">
          <Avatar name={`${user.first_name || ""} ${user.last_name || ""}`} />
          <div className="flex-1">
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="Écrivez votre commentaire…" data-testid="comment-input" />
            <div className="mt-2 flex justify-end">
              <Button onClick={() => post(null)} disabled={sending} className="bg-[#E05638] hover:bg-[#c94a2f]" data-testid="comment-submit">
                <Send size={15} className="mr-1.5" /> Publier
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-4 bg-orange-50 border border-orange-200 rounded-xl p-4 text-sm text-slate-700 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="comment-login-gate">
          <span className="flex-1">Connectez-vous pour participer à la discussion.</span>
          <div className="flex gap-2 shrink-0">
            <Link to="/connexion"><Button variant="outline" size="sm" data-testid="comment-login-btn">Se connecter</Button></Link>
            <Link to="/inscription"><Button size="sm" className="bg-[#E05638] hover:bg-[#c94a2f]" data-testid="comment-signup-btn">Créer un compte</Button></Link>
          </div>
        </div>
      )}

      <div className="mt-6 space-y-5">
        {roots.length === 0 && <div className="text-sm text-slate-400 text-center py-6">Soyez le premier à réagir à cet article.</div>}
        {roots.map((c) => (
          <div key={c.id} className="space-y-3">
            <CommentItem c={c} isReply={false} canDelete={c.mine || isAdmin} onLike={like} onReport={report} onDelete={del}
              onReply={(cc) => { const same = replyOpen === cc.id; setReplyOpen(same ? null : cc.id); setReplyText(same ? "" : `@${firstName(cc.author_name)} `); }}
              replyOpen={user ? replyOpen : null} replyText={replyText} setReplyText={setReplyText}
              submitReply={(pid) => post(pid, replyText)} sending={sending} />
            {repliesOf(c.id).map((r) => (
              <CommentItem key={r.id} c={r} isReply canDelete={r.mine || isAdmin} onLike={like} onReport={report} onDelete={del}
                onReply={() => {}} replyOpen={null} replyText="" setReplyText={() => {}} submitReply={() => {}} sending={sending} />
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}
